import { useCallback, useEffect, useState } from "react";
import { loadCpq, loadPages, loadProducts, loadRoutings, type CpqData, type PageEntry, type ProductEntry } from "./data/loader";
import Configurator from "./components/Configurator";
import ProductPicker from "./components/ProductPicker";
import { getLaunchContext, isOpenedFromNetsuite } from "./util/launchContext";
import { primeItemMapping } from "./util/netsuiteClient";
import { LEGACY_PRODUCT_SLUG, parseConfigSnapshot } from "./util/configSnapshot";
import { markChosen, markDataset } from "./util/sessionTrack";
import { pendingOppositeProduct } from "./util/pendingMirror";

// BOOT SEQUENCE (CPQ2)
//
//   Configure    -> the CPQ2 home page, where the user picks a configurator -- unless the
//                   launching form names one (products.json "forms": the O-Type and Box
//                   Type forms are always Natural Draft Burners), then straight into it.
//                   Also straight in when this tab reloaded to configure the opposite side
//                   of an MB housing (util/pendingMirror.ts).
//   Edit Config  -> straight into the configurator that BUILT the item, never the
//                   picker. The launch carries ?itemid=, the item carries its saved
//                   snapshot, and the snapshot carries the product slug — so the
//                   product is a fact about the item, not a question for the user.
//
// That ordering is why this is a state machine rather than one useEffect: the
// dataset cannot be fetched until the product is known, and on Edit Config the
// product is only knowable after a round-trip to NetSuite.
type BootState =
  | { phase: "resolving" } // deciding which configurator (Edit Config lookup)
  | { phase: "picking"; products: ProductEntry[]; pages: PageEntry[] } // CPQ2 home page
  | { phase: "loading"; slug: string } // fetching that product's dataset
  | { phase: "error"; message: string }
  | { phase: "ready"; data: CpqData };

export default function App() {
  // Only run when opened from NetSuite's Configure / Edit Config buttons, which
  // open cpq2 with window.open (so window.opener is set). A direct visit or a
  // shared link opened in a fresh tab has no opener -> blocked. (Same signal as
  // the Cancel button: no opener, no access.)
  const authorized = isOpenedFromNetsuite();
  const [state, setState] = useState<BootState>({ phase: "resolving" });
  // After a while still loading, offer a manual reload — a stalled resource can
  // leave the app on the spinner longer than the fetch retries take to give up.
  const [slow, setSlow] = useState(false);

  const fail = useCallback((err: unknown) => {
    setState({
      phase: "error",
      message: err instanceof Error ? err.message : String(err),
    });
  }, []);

  // ---- Step 1: which configurator? -------------------------------------------
  useEffect(() => {
    if (!authorized) return; // blocked — don't fetch anything
    let cancelled = false;
    const itemId = getLaunchContext().itemId;

    // Configure: no item, so no product is implied. Offer the picker.
    if (!itemId) {
      Promise.all([loadProducts(), loadPages()])
        .then(([products, pages]) => {
          if (cancelled) return;
          // A single configurator makes the picker a page with one button on it —
          // skip straight into it. The home page earns its place once there is a
          // real choice to make (another configurator, or a tool like the Laser Job).
          const enabled = products.filter((p) => p.enabled);
          const formId = Number(getLaunchContext().formId);
          const byForm = Number.isFinite(formId) ? enabled.find((p) => (p.forms ?? []).includes(formId)) : undefined;
          const pending = pendingOppositeProduct();
          const byPending = pending ? enabled.find((p) => p.slug === pending) : undefined;
          if (byPending) setState({ phase: "loading", slug: byPending.slug });
          else if (byForm) setState({ phase: "loading", slug: byForm.slug });
          else if (enabled.length === 1 && pages.length === 0) setState({ phase: "loading", slug: enabled[0].slug });
          else setState({ phase: "picking", products, pages });
        })
        .catch((e) => !cancelled && fail(e));
      return () => {
        cancelled = true;
      };
    }

    // Edit Config: read the launch item to learn which configurator built it.
    // primeItemMapping stashes the payload so AutoLoadItem does not refetch it a
    // moment later (NetSuite REST is one-request-at-a-time — see netsuiteClient).
    Promise.all([loadProducts(), primeItemMapping(itemId).catch(() => null)])
      .then(async ([products, item]) => {
        if (cancelled) return;
        // No snapshot at all (a legacy item that only has custitem_mapping, or an
        // item lookup that failed) resolves to the legacy configurator — which is
        // right: nothing without a stamp was built anywhere else.
        let slug = LEGACY_PRODUCT_SLUG;
        if (item && item.config) {
          try {
            // Only the product stamp is wanted here. The answers are AutoLoadItem's
            // job, once the right dataset is actually in place to restore into.
            slug = (await parseConfigSnapshot(item.config)).product;
          } catch {
            // Unreadable snapshot: fall back to the legacy configurator and let
            // AutoLoadItem report the real problem, which it does far better than
            // a dead boot screen would. It also still has the mapping fallback.
            slug = LEGACY_PRODUCT_SLUG;
          }
        }
        if (cancelled) return;
        // A stamp naming a configurator this build does not have is worth saying
        // out loud — the alternative is a 404 on questions.json, which reads as a
        // deployment fault rather than "that product isn't here".
        if (!products.some((p) => p.slug === slug)) {
          fail(
            new Error(
              `This item was built in a configurator this version does not have ` +
                `("${slug}"). Known configurators: ${products.map((p) => p.slug).join(", ")}.`
            )
          );
          return;
        }
        setState({ phase: "loading", slug });
      })
      .catch((e) => !cancelled && fail(e));
    return () => {
      cancelled = true;
    };
  }, [authorized, fail]);

  // ---- Step 2: load that configurator's dataset ------------------------------
  const slugToLoad = state.phase === "loading" ? state.slug : null;
  useEffect(() => {
    if (!slugToLoad) return;
    let cancelled = false;
    // Boot timing for the 'ready' row (util/sessionTrack). Diagnostic only.
    markChosen(slugToLoad);
    loadCpq(slugToLoad)
      .then((data) => {
        markDataset();
        if (!cancelled) setState({ phase: "ready", data });
        // Warm routings.json NOW rather than at Save & Submit.
        //
        // It is deliberately not part of the initial load (7.6 MB raw for Natural Draft), but
        // SaveSubmitButton awaits it as the FIRST thing it does, so the whole download sat on
        // the critical path with the user watching a spinner -- against an origin measured at
        // 13.95 s for a 1.8 KB file, with a 60 s per-attempt timeout behind it.
        //
        // Safe to fire and forget: loadRoutings memoises per slug, so the submit-time call
        // reuses this exact promise, and a rejection removes itself from the cache so that
        // call retries cleanly. Only Natural Draft actually pays anything -- flame-cells,
        // flange and pilot all ship an empty routings list.
        void loadRoutings().catch(() => {
          /* prefetch only: the submit path will surface a real failure if it matters */
        });
      })
      .catch((e) => !cancelled && fail(e));
    return () => {
      cancelled = true;
    };
  }, [slugToLoad, fail]);

  // Surface the "taking a while" hint after 12s of waiting; clear once past it.
  const waiting = state.phase === "resolving" || state.phase === "loading";
  useEffect(() => {
    if (!waiting) {
      setSlow(false);
      return;
    }
    const t = window.setTimeout(() => setSlow(true), 12000);
    return () => window.clearTimeout(t);
  }, [waiting]);

  if (!authorized) {
    return (
      <div className="cpq-blocked">
        <h1>Open from NetSuite</h1>
        <p>
          This configurator can only be opened from the <strong>Configure</strong>{" "}
          or <strong>Edit Config</strong> button on a NetSuite sales order. If you
          reached this page some other way, reopen it from NetSuite.
        </p>
      </div>
    );
  }

  if (state.phase === "picking") {
    return (
      <ProductPicker
        products={state.products}
        pages={state.pages}
        onPick={(slug) => setState({ phase: "loading", slug })}
      />
    );
  }

  if (waiting) {
    return (
      <div className="cpq-loading">
        <div className="cpq-spinner" />
        {/* App startup (loading rules/data) — NOT restoring a saved config.
            The saved-config restore, if any, is announced by AutoLoadItem. */}
        <p>Starting Flameco CPQ…</p>
        {slow && (
          <div className="cpq-loading-slow">
            <p>This is taking longer than usual — a resource may be stalled.</p>
            <button type="button" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        )}
      </div>
    );
  }
  if (state.phase === "error") {
    return (
      <div className="cpq-error">
        <h1>Failed to load CPQ data</h1>
        <pre>{state.message}</pre>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
  return <Configurator data={state.data} />;
}
